import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Shield, RefreshCw, AlertTriangle, CheckCircle, Download } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import * as api from '../../api';

const PILLAR_COLORS = [
  '#2563EB', '#7C3AED', '#DB2777', '#DC2626',
  '#EA580C', '#16A34A', '#0891B2', '#6B7280', '#374151',
];

export default function ArchitectureApp() {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.getArchitecture();
      setContent(res.data.content);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (user?.role !== 'super_admin') {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <AlertTriangle size={36} style={{ color: '#DC2626' }} />
        <p className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>
          Accès réservé au super-administrateur.
        </p>
      </div>
    );
  }

  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'ARCHITECTURE.md'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="architecture-page">

      {/* En-tête */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #1E40AF, #7C3AED)' }}>
            <Shield size={18} color="white" />
          </div>
          <div>
            <h2 className="text-lg font-bold" style={{ color: 'var(--text-secondary)' }}>
              Architecture & Sécurité
            </h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              Constitution technique — lecture seule
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownload} disabled={!content}
            data-testid="architecture-download"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border hover:opacity-80 transition-opacity"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)', background: 'white' }}>
            <Download size={12} /> Télécharger .md
          </button>
          <button
            onClick={load} disabled={loading}
            data-testid="architecture-refresh"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold hover:opacity-80 transition-opacity"
            style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Bandeau statut */}
      {!loading && !error && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg mb-6"
          style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
          <CheckCircle size={14} style={{ color: '#16A34A', flexShrink: 0 }} />
          <span className="text-xs font-medium" style={{ color: '#15803D' }}>
            Constitution chargée — 9 piliers actifs. Tout code doit s'y conformer.
          </span>
        </div>
      )}

      {/* Contenu */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-7 h-7 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
        </div>
      ) : error ? (
        <div className="flex items-center gap-3 p-4 rounded-lg"
          style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}>
          <AlertTriangle size={16} style={{ color: '#DC2626' }} />
          <span className="text-sm" style={{ color: '#B91C1C' }}>{error}</span>
        </div>
      ) : (
        <div
          data-testid="architecture-content"
          className="prose prose-sm max-w-none rounded-xl p-8"
          style={{
            background: 'white',
            border: '1px solid var(--border)',
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              // H1 principal
              h1: ({ children }) => (
                <h1 style={{
                  fontSize: '1.4rem', fontWeight: 800, marginBottom: '0.5rem',
                  color: 'var(--text-secondary)',
                  borderBottom: '3px solid #1E40AF',
                  paddingBottom: '0.5rem',
                }}>
                  {children}
                </h1>
              ),
              // H2 = piliers numérotés
              h2: ({ children, ...props }) => {
                const text = String(children);
                const pillarNum = parseInt(text.match(/Pilier (\d+)/)?.[1] || '0', 10);
                const color = pillarNum ? PILLAR_COLORS[(pillarNum - 1) % PILLAR_COLORS.length] : '#374151';
                return (
                  <h2 style={{
                    fontSize: '1rem', fontWeight: 700,
                    color: 'white',
                    background: color,
                    borderRadius: '8px',
                    padding: '8px 16px',
                    marginTop: '2rem',
                    marginBottom: '1rem',
                    letterSpacing: '0.02em',
                  }}>
                    {children}
                  </h2>
                );
              },
              // H3 sous-sections
              h3: ({ children }) => (
                <h3 style={{
                  fontSize: '0.85rem', fontWeight: 700,
                  color: 'var(--text-secondary)',
                  borderLeft: '3px solid var(--primary-solid)',
                  paddingLeft: '10px',
                  marginTop: '1.2rem', marginBottom: '0.5rem',
                }}>
                  {children}
                </h3>
              ),
              // Blocs de code
              code: ({ inline, className, children }) => {
                if (inline) return (
                  <code style={{
                    fontSize: '0.75rem',
                    background: '#F1F5F9',
                    color: '#1E40AF',
                    padding: '1px 5px',
                    borderRadius: 4,
                    fontFamily: 'JetBrains Mono, monospace',
                  }}>
                    {children}
                  </code>
                );
                return (
                  <pre style={{
                    background: '#0F172A',
                    color: '#E2E8F0',
                    borderRadius: '8px',
                    padding: '16px',
                    overflow: 'auto',
                    fontSize: '0.72rem',
                    fontFamily: 'JetBrains Mono, monospace',
                    lineHeight: 1.6,
                    margin: '0.75rem 0',
                    border: '1px solid #1E293B',
                  }}>
                    <code>{children}</code>
                  </pre>
                );
              },
              // Tableaux
              table: ({ children }) => (
                <div style={{ overflowX: 'auto', margin: '0.75rem 0' }}>
                  <table style={{
                    borderCollapse: 'collapse', width: '100%',
                    fontSize: '0.78rem',
                  }}>
                    {children}
                  </table>
                </div>
              ),
              th: ({ children }) => (
                <th style={{
                  background: '#F1F5F9',
                  padding: '8px 12px',
                  border: '1px solid #E2E8F0',
                  fontWeight: 700, textAlign: 'left',
                  color: 'var(--text-secondary)',
                }}>
                  {children}
                </th>
              ),
              td: ({ children }) => (
                <td style={{
                  padding: '7px 12px',
                  border: '1px solid #E2E8F0',
                  color: 'var(--text)',
                }}>
                  {children}
                </td>
              ),
              // Blockquote (avertissements)
              blockquote: ({ children }) => (
                <blockquote style={{
                  borderLeft: '4px solid #7C3AED',
                  background: '#FAF5FF',
                  padding: '10px 16px',
                  margin: '0.75rem 0',
                  borderRadius: '0 8px 8px 0',
                  fontSize: '0.8rem',
                  color: '#6D28D9',
                  fontStyle: 'italic',
                }}>
                  {children}
                </blockquote>
              ),
              // Listes
              li: ({ children }) => (
                <li style={{ marginBottom: '0.2rem', color: 'var(--text)', fontSize: '0.82rem' }}>
                  {children}
                </li>
              ),
              // Séparateur
              hr: () => (
                <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '2rem 0' }} />
              ),
              // Paragraphes
              p: ({ children }) => (
                <p style={{ fontSize: '0.82rem', color: 'var(--text)', lineHeight: 1.7, marginBottom: '0.5rem' }}>
                  {children}
                </p>
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
}
